<i18n>
{
	"en": {
		"editTemplate": "Edit deviation cause",
		"newDeviationCause": "Add deviation cause",
		"deleteDeviationCause": "Remove deviation cause | Remove deviation cause | Remove deviation causes",
		"noDeviationCausesFound": "No Deviation Causes...",
		"header": "Deviation Causes",
		"code": "Deviation Code",
		"description": "Description",
		"availableFor": "Available for Type",
		"size": "Size",
		"editedDate": "Edited Date",
		"failedToFetch": "Failed to get deviation-cause data",
		"failedToDelete": "Failed to delete deviation cause",
		"deletedDeviationCause": "Deleted deviation cause | Deleted deviation cause | Deleted {count} deviation causes"
	},
	"no": {
		"editTemplate": "Rediger avviksårsak",
		"newDeviationCause": "Legg til avviksårsak",
		"deleteDeviationCause": "Fjern avviksårsak | Fjern avviksårsak | Fjern avviksårsaker",
		"noDeviationCausesFound": "Ingen avviksårsaker...",
		"header": "Avviksårsaker",
		"code": "Avvik, kode",
		"description": "Beskrivelse",
		"availableFor": "Tilgjengelig for avvikstype",
		"size": "Størrelse",
		"editedDate": "Endret dato",
		"failedToFetch": "Kunne ikke hente avviksårsak data",
		"failedToDelete": "Kunne ikke slette avviksårsak",
		"deletedDeviationCause": "Avviksårsaken ble slettet | Avviksårsaken ble slettet | Slettet {count} avviksårsaker"
	}
}
</i18n>
<template>
	<t-vertical-panel class="deviation-list">
		<div slot="header" class="deviation-list-header">
			<t-panel-header size="md" type="bp" icon="info-sign" >
				{{ $tc('header', deviationCausesFound) }}
			</t-panel-header>
		</div>
		<div slot="sub-header" class="deviation-list-sub-header">
			<t-button
				:disabled="true"
				iconBefore="annotation"
				appearance="minimal"
				@click="editTemplate()"
			>
				<!-- :disabled="rowsSelected !== 1" -->
				{{ $t('editTemplate') }}</t-button>
			<t-button
				:disabled="true"
				iconBefore="trash"
				appearance="minimal"
				width="150px"
				type="bp"
				@click="onDeleteDeviationCause"
			>
				<!-- :disabled="rowsSelected == 0" -->
				{{$tc('deleteDeviationCause', rowsSelected)}}</t-button>
			<t-button
				:disabled="true"
				appearance="primary"
				width="150px"
				@click="onAddDeviationCause"
			>{{$t('newDeviationCause')}}</t-button>
		</div>
		<div class="deviation-list-content">
			<t-grid
				v-if="deviationCausesFound"
				ref="grid"
				:columnDefinitions="[
					{
						field: 'code', displayName: $t('code'),
						width: 200,
					},{
						field: 'description', displayName: $t('description')
					},{
						field: 'availableFor', displayName: $t('availableFor')
					},{
						field: 'editedDate.date', displayName: $t('editedDate'),
						valueGetter: item => dateFormatter(item.editedDate.date),
						width: 200, resizable: false,
						type: 'date',
						comparableValueGetter: item => moment(item.editedDate.date, storageDateFormat).toISOString()
					}
				]"
				:defaultColumnDefinition="defaultColumnDefinition"
				:rowHeight="40"
				:items="deviationCauses"
				:selectableMode="['checkbox']"
				localStorageId="deviationCausesSearchList"
				selectable
				@selected="onRowsSelected"
				@rowDoubleClick="editTemplate($event)"
				@enter="editTemplate()"
			/>
			<t-no-content-panel v-else :text="$t('noDeviationCausesFound')" icon="order" />
			<t-overlay
				:isShown="loading"
				absolute
				loader
			/>
		</div>
		<EditDeviationCause/>
	</t-vertical-panel>
</template>

<script>
import { mapState, mapActions } from './store/deviations.store.js';
import { mapState as settingsState } from '@/modules/settings/store/settings.store.js';
import Grid from '@/components/evergreen/timpex/grid/TGrid';
import { storageDateFormat } from '@/core';
import moment from 'moment';
import EditDeviationCause from './EditDeviationCause';

export default {
	name: 'DeviationCauses',
	components: { TGrid: Grid, EditDeviationCause },
	data() {
		return {
			defaultColumnDefinition: {
				resizable: true,
				headerFilterInputWithButton: true,
			},
			rowsSelected: 0,
		};
	},
	computed: {
		...mapState([
			'deviationCauses',
			'loading'
		]),
		...settingsState(['locale',]),
		deviationCausesFound() {
			return this.deviationCauses.length;
		},
	},
	async mounted() {
		try {
			await this.getDeviationCauses();
		} catch (e) {
			this.$notify({
				type: 'error',
				title: this.$t('failedToFetch')
			});
		}
	},
	methods: {
		...mapActions([
			'getDeviationCauses',
			'setDeviationCauseTemplate',
			'setDeviationCauseEditId',
			'showEditDeviationCause',
			'deleteDeviationCauses',
		]),
		dateFormatter(date) {
			if (!date) return '';
			return moment(date, storageDateFormat).format('LLL');
		},
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		editTemplate(id) {
			let deviationCause = undefined;
			if (id) {
				deviationCause = this.deviationCauses.find( (x) => x.id === id);
			} else {
				let selectedItems = this.$refs.grid.getSelectedItems();
				if (selectedItems.length > 1) return;
				[deviationCause] = selectedItems;
			}
			if (deviationCause && deviationCause.id) {
				this.setDeviationCauseTemplate(deviationCause);
				this.setDeviationCauseEditId(deviationCause.id);
			}
			this.showEditDeviationCause(true);
		},
		async onDeleteDeviationCause() {
			let selectedDeviationCauses = this.$refs.grid.getSelectedItems();
			if (!selectedDeviationCauses) return;
			try {
				await this.deleteDeviationCauses(selectedDeviationCauses);
				this.$notify({
					type: 'success',
					title: this.$tc('deletedDeviationCause', selectedDeviationCauses.length)
				});
				this.rowsSelected = 0;
			} catch (e) {
				this.$notify({
					type: 'error',
					title: this.$t('failedToDelte')
				});
			}
		},
		async onAddDeviationCause() {
			this.showEditDeviationCause(true);
		}
	},
};
</script>


<style lang="scss" scoped>
.deviation-list {
	.deviation-list-header {
		height: 100%;
		display: flex;
		align-items: center;
		padding-left: 15px;
	}
	.deviation-list-sub-header {
		display: flex;
		padding: 0px 15px;
		height: 100%;
		align-items: center;
		justify-content: flex-end;

		> button {
			margin: 10px;
		}
	}
	.deviation-list-content {
		height: 100%;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 0px 5px
	}
}
</style>
